import React from 'react';
import { motion } from 'framer-motion';
import CountUp from 'react-countup';
import { Trophy, Users, Briefcase, Handshake, ShieldCheck, Globe, Certificate, Gear } from '@phosphor-icons/react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../i18n/translations';

const StatCard = ({ icon: Icon, value, suffix, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.6, delay }}
    viewport={{ once: true }}
    className="text-center p-8 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#FF6B00]/30 transition-colors"
  >
    <Icon size={36} className="text-[#FF6B00] mx-auto mb-4" weight="duotone" />
    <div className="text-4xl sm:text-5xl font-bold text-white mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
      <CountUp end={value} duration={2.5} suffix={suffix} enableScrollSpy scrollSpyOnce />
    </div>
    <p className="text-sm text-white/60 uppercase tracking-wider">{label}</p>
  </motion.div>
);

export const WhyChooseUs = () => {
  const { t, language } = useLanguage();

  const stats = [
    { icon: Briefcase, value: companyInfo.stats.experience, suffix: '+', label: t.why.experience },
    { icon: Trophy, value: companyInfo.stats.projects, suffix: '+', label: t.why.projects },
    { icon: Users, value: companyInfo.stats.clients, suffix: '%', label: t.why.clients },
  ];

  const reasons = language === 'fr' ? [
    { number: '01', icon: ShieldCheck, title: 'Expertise Technique Certifiée', description: 'Ingénieurs certifiés, protocoles ISO 9001:2015 et ISO 45001:2018 appliqués sur chaque chantier. Nos méthodes de calcul suivent les Eurocodes et les normes ASTM.' },
    { number: '02', icon: Globe, title: 'Couverture Nationale', description: '12 provinces couvertes avec une capacité de mobilisation sous 72h. Logistique intégrée pour les zones les plus reculées du territoire congolais.' },
    { number: '03', icon: Certificate, title: 'Standards Internationaux', description: 'Conformité aux normes Eurocodes, ASTM, ISO et FIDIC. Laboratoire géotechnique certifié réalisant 200+ essais par mois.' },
    { number: '04', icon: Gear, title: 'Solutions Intégrées', description: '6 pôles d\'expertise — du forage à la consultance — pour un accompagnement complet de vos projets, de la faisabilité à la réception définitive.' },
  ] : [
    { number: '01', icon: ShieldCheck, title: 'Certified Technical Expertise', description: 'Certified engineers, ISO 9001:2015 and ISO 45001:2018 protocols applied on every site. Our calculation methods follow Eurocodes and ASTM standards.' },
    { number: '02', icon: Globe, title: 'National Coverage', description: '12 provinces covered with mobilization capability within 72h. Integrated logistics for the most remote areas of Congolese territory.' },
    { number: '03', icon: Certificate, title: 'International Standards', description: 'Compliance with Eurocodes, ASTM, ISO and FIDIC standards. Certified geotechnical laboratory performing 200+ tests per month.' },
    { number: '04', icon: Gear, title: 'Integrated Solutions', description: '6 areas of expertise — from drilling to consulting — for complete project support, from feasibility to final acceptance.' },
  ];

  return (
    <section className="py-24 lg:py-32 bg-[#0B1320] relative overflow-hidden" data-testid="why-section">
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B00]/5 rounded-full blur-[120px]" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF6B00]/3 rounded-full blur-[80px]" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} viewport={{ once: true }} className="text-center mb-16">
          <p className="text-[#FF6B00] text-xs sm:text-sm font-medium uppercase tracking-[0.2em] mb-4">{t.why.title}</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }} data-testid="why-title">{t.why.subtitle}</h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-16">
          {stats.map((stat, index) => (
            <StatCard key={stat.label} icon={stat.icon} value={stat.value} suffix={stat.suffix} label={stat.label} delay={index * 0.1} />
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-4 mb-16">
          {reasons.map((reason, index) => (
            <motion.div key={reason.number} initial={{ opacity: 0, x: index % 2 === 0 ? -20 : 20 }} whileInView={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }} viewport={{ once: true }} className="flex gap-6 p-6 bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#FF6B00]/20 transition-colors">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-[#FF6B00]/10 flex items-center justify-center">
                  <reason.icon size={24} className="text-[#FF6B00]" weight="bold" />
                </div>
              </div>
              <div>
                <h4 className="text-lg font-semibold text-white mb-2">{reason.title}</h4>
                <p className="text-sm text-white/60 leading-relaxed">{reason.description}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} viewport={{ once: true }} className="pt-16 border-t border-white/10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Handshake size={24} className="text-[#FF6B00]" weight="bold" />
                <h3 className="text-lg font-semibold text-white">{t.why.partners.title}</h3>
              </div>
              <p className="text-sm text-white/60">{t.why.partners.desc}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              {companyInfo.partners.map((partner, index) => (
                <div key={index} className="px-5 py-2.5 bg-white/5 backdrop-blur-sm border border-white/10 text-white/80 text-sm font-medium hover:border-[#FF6B00]/30 transition-colors">
                  {partner}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
